import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, getAuthHeader } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Package, RefreshCw, Search, Eye, ShieldCheck, Send, Upload, Archive,
  FileSpreadsheet, CheckCircle2, Loader2, Clock,
} from "lucide-react";
import { StatusBadge, ShipmentDetail } from "./UploadPanel";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const ShipmentsPanel = ({ user }) => {
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusLabels, setStatusLabels] = useState({});
  const [detailShipment, setDetailShipment] = useState(null);
  const [kpis, setKpis] = useState(null);

  // Attach doc dialog
  const [attachOpen, setAttachOpen] = useState(false);
  const [attachTarget, setAttachTarget] = useState(null);
  const [attachKind, setAttachKind] = useState("packing_list");
  const [attachFile, setAttachFile] = useState(null);
  const [attaching, setAttaching] = useState(false);

  // Review dialog
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewTarget, setReviewTarget] = useState(null); // {docRef, filename, kind}
  const [reviewStatus, setReviewStatus] = useState("approved");
  const [reviewRemarks, setReviewRemarks] = useState("");
  const [reviewing, setReviewing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sh, cat, k] = await Promise.all([
        axios.get(`${API}/shipments?scope=all`, { headers: getAuthHeader() }),
        axios.get(`${API}/shipments/status-catalog`, { headers: getAuthHeader() }),
        axios.get(`${API}/shipments-kpis`, { headers: getAuthHeader() }).catch(() => ({ data: null })),
      ]);
      setShipments(sh.data.shipments || []);
      setStatusLabels(cat.data.labels || {});
      setKpis(k.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load shipments");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (id) => {
    try {
      const r = await axios.get(`${API}/shipments/${id}`, { headers: getAuthHeader() });
      setDetailShipment(r.data);
    } catch (e) { toast.error("Failed to load shipment"); }
  };

  const refreshDetail = async (id) => {
    try {
      const r = await axios.get(`${API}/shipments/${id}`, { headers: getAuthHeader() });
      setDetailShipment(r.data);
    } catch { /* ignore */ }
  };

  const setStatus = async (id, status, message = "") => {
    try {
      await axios.patch(`${API}/shipments/${id}/status`,
                        { status, message }, { headers: getAuthHeader() });
      toast.success(`Status → ${statusLabels[status] || status}`);
      await load();
      await refreshDetail(id);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const toggleQA = async (id, verified) => {
    try {
      await axios.post(`${API}/shipments/${id}/qa-verify`,
                       { verified }, { headers: getAuthHeader() });
      toast.success(verified ? "QA Verified" : "QA verification removed");
      await load();
      await refreshDetail(id);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const share = async (id) => {
    try {
      await axios.post(`${API}/shipments/${id}/share`, {}, { headers: getAuthHeader() });
      toast.success("Shared with Processing Team ✓");
      await load();
      await refreshDetail(id);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const openAttach = (shipment, kind = "packing_list") => {
    setAttachTarget(shipment);
    setAttachKind(kind);
    setAttachFile(null);
    setAttachOpen(true);
  };

  const submitAttach = async () => {
    if (!attachFile || !attachTarget) return;
    setAttaching(true);
    try {
      const fd = new FormData();
      fd.append("file", attachFile);
      fd.append("kind", attachKind);
      await axios.post(`${API}/shipments/${attachTarget.id}/attach-document`, fd,
                       { headers: { ...getAuthHeader() } });
      toast.success("Document attached to shipment");
      setAttachOpen(false);
      setAttachFile(null);
      await load();
      await refreshDetail(attachTarget.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Attach failed");
    } finally { setAttaching(false); }
  };

  const openReview = (docRef, meta) => {
    setReviewTarget({ docRef, ...meta });
    setReviewStatus(meta?.currentStatus || "approved");
    setReviewRemarks(meta?.currentRemarks || "");
    setReviewOpen(true);
  };

  const submitReview = async () => {
    if (!reviewTarget || !detailShipment) return;
    setReviewing(true);
    try {
      await axios.post(
        `${API}/shipments/${detailShipment.id}/documents/${reviewTarget.docRef}/review`,
        { status: reviewStatus, remarks: reviewRemarks },
        { headers: getAuthHeader() },
      );
      const labelMap = {
        approved: "Approved", rejected: "Rejected",
        needs_replacement: "Needs Replacement", pending: "Pending Review",
      };
      toast.success(`${labelMap[reviewStatus]} — ${reviewTarget.filename}`);
      setReviewOpen(false);
      await load();
      await refreshDetail(detailShipment.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Review failed");
    } finally { setReviewing(false); }
  };

  const filtered = shipments.filter(s => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      (s.shipment_no || "").toLowerCase().includes(q) ||
      (s.company_name || "").toLowerCase().includes(q) ||
      (s.customer_name || "").toLowerCase().includes(q) ||
      (s.invoice_no || "").toLowerCase().includes(q) ||
      (s.status || "").toLowerCase().includes(q)
    );
  });

  return (
    <div className="p-4 md:p-6 space-y-4 min-h-full bg-slate-50" data-testid="shipments-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Package className="w-7 h-7 text-purple-600" />
            Shipments Workflow — Review & Generate
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Review packing slips uploaded by the Processing Team, generate export documents,
            QA verify, and share back.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)}
                   className="pl-8 w-56 h-9 text-sm" data-testid="shipments-search" />
          </div>
          <Button variant="outline" size="sm" onClick={load} data-testid="shipments-refresh">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* KPI Dashboard */}
      {kpis && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2" data-testid="shipments-kpis">
          {[
            { label: "New Packing Slips", key: "new_slips", color: "bg-blue-100 text-blue-800" },
            { label: "Under Review", key: "under_review", color: "bg-amber-100 text-amber-800" },
            { label: "QA Pending", key: "qa_pending", color: "bg-violet-100 text-violet-800" },
            { label: "Ready to Share", key: "ready_to_share", color: "bg-emerald-100 text-emerald-800" },
            { label: "Shared Today", key: "shared_today", color: "bg-teal-100 text-teal-800" },
            { label: "Total Shared", key: "shared", color: "bg-teal-50 text-teal-700" },
            { label: "Printed", key: "printed", color: "bg-slate-200 text-slate-800" },
            { label: "Archived", key: "archived", color: "bg-slate-100 text-slate-500" },
          ].map(c => (
            <div key={c.key} className={`rounded-lg p-2.5 ${c.color} border-l-4 border-current`}
                 data-testid={`kpi-${c.key}`}>
              <div className="text-[10px] uppercase tracking-wide font-medium opacity-80">{c.label}</div>
              <div className="text-2xl font-bold mt-0.5">{kpis[c.key] ?? 0}</div>
            </div>
          ))}
        </div>
      )}

      <Card className="bg-white/80 border-0 shadow-sm">
        <CardContent className="p-0">
          {filtered.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-500" data-testid="shipments-empty">
              {loading ? "Loading…" : "No shipments yet. Processing Team will create these via the Upload Panel."}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="text-left px-3 py-2">Shipment #</th>
                    <th className="text-left px-3 py-2">Company</th>
                    <th className="text-left px-3 py-2">Customer</th>
                    <th className="text-left px-3 py-2">Invoice</th>
                    <th className="text-left px-3 py-2">Uploaded</th>
                    <th className="text-left px-3 py-2">Status</th>
                    <th className="text-left px-3 py-2">QA</th>
                    <th className="text-right px-3 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(s => (
                    <tr key={s.id} className="border-b hover:bg-slate-50" data-testid={`shipments-row-${s.id}`}>
                      <td className="px-3 py-2 font-mono text-xs">{s.shipment_no}</td>
                      <td className="px-3 py-2 text-xs">{s.company_name || "—"}</td>
                      <td className="px-3 py-2 text-xs">{s.customer_name || "—"}</td>
                      <td className="px-3 py-2 text-xs">{s.invoice_no || "—"}</td>
                      <td className="px-3 py-2 text-xs text-slate-500">
                        <div>{fmtDate(s.created_at)}</div>
                        <div className="text-[10px]">by {s.created_by_email}</div>
                      </td>
                      <td className="px-3 py-2">
                        <StatusBadge status={s.status} label={statusLabels[s.status] || s.status} />
                      </td>
                      <td className="px-3 py-2">
                        {s.qa_verified ? <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                                        : <span className="text-slate-300 text-xs">—</span>}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]"
                                onClick={() => openDetail(s.id)}
                                data-testid={`shipments-detail-${s.id}`}>
                          <Eye className="w-3 h-3 mr-1" /> Open
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail dialog with workflow actions */}
      <ShipmentDetail
        shipment={detailShipment}
        onClose={() => setDetailShipment(null)}
        statusLabels={statusLabels}
        onReviewClick={(docRef, meta) => openReview(docRef, meta)}
        onChanged={async (id) => { await load(); await refreshDetail(id); }}
        actions={
          detailShipment && !detailShipment.shared && detailShipment.status !== "archived" && (
            <div className="flex flex-wrap gap-2 mr-auto">
              {detailShipment.status === "uploaded" && (
                <Button size="sm" variant="outline"
                        onClick={() => setStatus(detailShipment.id, "under_review", "Started review")}
                        data-testid="detail-mark-review">
                  Mark Under Review
                </Button>
              )}
              <Button size="sm" variant="outline"
                      onClick={() => openAttach(detailShipment, "packing_list")}
                      data-testid="detail-attach-pl"
                      className="text-violet-700 border-violet-200">
                <Upload className="w-4 h-4 mr-1" /> Attach Packing List
              </Button>
              <Button size="sm" variant="outline"
                      onClick={() => openAttach(detailShipment, "pack_team_print")}
                      data-testid="detail-attach-ptp"
                      className="text-violet-700 border-violet-200">
                <Upload className="w-4 h-4 mr-1" /> Attach Pack Team Print
              </Button>
              {(detailShipment.generated_docs || []).length > 0 && !detailShipment.qa_verified && (
                <Button size="sm" variant="outline"
                        onClick={() => toggleQA(detailShipment.id, true)}
                        className="text-emerald-700 border-emerald-200"
                        data-testid="detail-qa-verify">
                  <ShieldCheck className="w-4 h-4 mr-1" /> QA Verify
                </Button>
              )}
              {detailShipment.qa_verified && (
                <Button size="sm"
                        onClick={() => share(detailShipment.id)}
                        className="bg-teal-600 hover:bg-teal-700 text-white"
                        data-testid="detail-share">
                  <Send className="w-4 h-4 mr-1" /> OK &amp; Share
                </Button>
              )}
            </div>
          )
        }
      />

      {/* Review document dialog */}
      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent className="max-w-md" data-testid="review-dialog">
          <DialogHeader>
            <DialogTitle>Review Document</DialogTitle>
            <DialogDescription>
              {reviewTarget?.filename} <span className="text-slate-400">· {reviewTarget?.kind}</span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Decision</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                {[
                  { v: "approved", label: "Approved", cls: "border-emerald-200 text-emerald-700 hover:bg-emerald-50" },
                  { v: "rejected", label: "Rejected", cls: "border-red-200 text-red-700 hover:bg-red-50" },
                  { v: "needs_replacement", label: "Needs Replacement", cls: "border-amber-200 text-amber-700 hover:bg-amber-50" },
                  { v: "pending", label: "Pending", cls: "border-slate-200 text-slate-700 hover:bg-slate-50" },
                ].map(opt => (
                  <Button key={opt.v} type="button" variant="outline"
                          className={`${opt.cls} ${reviewStatus === opt.v ? "ring-2 ring-offset-1 ring-current font-semibold" : ""}`}
                          onClick={() => setReviewStatus(opt.v)}
                          data-testid={`review-status-${opt.v}`}>
                    {opt.label}
                  </Button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs">
                Remarks {reviewStatus !== "approved" && <span className="text-red-500">*</span>}
              </Label>
              <Textarea value={reviewRemarks}
                        onChange={(e) => setReviewRemarks(e.target.value)}
                        rows={3}
                        placeholder={reviewStatus === "rejected" || reviewStatus === "needs_replacement"
                          ? "Explain what needs to change — the Processing Team will be notified with this message."
                          : "Optional note"}
                        data-testid="review-remarks" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewOpen(false)} disabled={reviewing}>Cancel</Button>
            <Button onClick={submitReview}
                    disabled={reviewing || ((reviewStatus === "rejected" || reviewStatus === "needs_replacement") && !reviewRemarks.trim())}
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                    data-testid="review-submit">
              {reviewing ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Saving…</> : "Save Review"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Attach document dialog */}
      <Dialog open={attachOpen} onOpenChange={(o) => { if (!o) setAttachOpen(false); }}>
        <DialogContent className="max-w-md" data-testid="attach-dialog">
          <DialogHeader>
            <DialogTitle>Attach Generated Document</DialogTitle>
            <DialogDescription>
              Link the file to shipment <strong>{attachTarget?.shipment_no}</strong>.
              Generate the {attachKind.replace("_", " ")} in its dedicated panel, download it,
              then upload it here to attach it to this shipment.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Document Type</Label>
              <div className="mt-1 text-xs bg-slate-50 rounded p-2 font-mono">{attachKind}</div>
            </div>
            <div>
              <Label className="text-xs">File</Label>
              <Input type="file"
                     onChange={(e) => setAttachFile(e.target.files?.[0] || null)}
                     data-testid="attach-file" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAttachOpen(false)} disabled={attaching}>Cancel</Button>
            <Button onClick={submitAttach} disabled={!attachFile || attaching}
                    className="bg-violet-600 hover:bg-violet-700 text-white"
                    data-testid="attach-submit">
              {attaching ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Attaching…</>
                        : <>Attach & Link</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ShipmentsPanel;
